// LifeIQ Books — product data configuration
// This is the single source of truth for the Books section.
//
// COMMAND 02 RECONCILIATION (2026-09-06): all titles, Telugu titles,
// subtitles, page counts, chapter lists, feature bullets and parent notes
// below are derived from the four authoritative Sale Ready PDFs
// (Chinnu's Talking Adventures, K Sudheer Bose, LifeIQ Publications,
// First Edition 2026). Covers = PDF page 1 renders; preview pages are
// extracted from the actual PDFs. Nothing below is invented placeholder.
//
// COMMAND 03 STATUS (owner-approved):
//  - Launch-offer pricing: Books 1–3 ₹99 → ₹49, Book 4 ₹149 → ₹99,
//    Collection ₹446 → ₹199 (save ₹247). Label: "Launch Offer".
//  - LIVE Razorpay payment links wired exactly as supplied.

export const ADMIN_PLACEHOLDER = "ADMIN_PLACEHOLDER";
export const RAZORPAY_LINK_PENDING = "RAZORPAY_LINK_PENDING";

// Bilingual positioning (owner requirement: every Chinnu book/bundle must
// visibly communicate English+Telugu before a customer clicks Buy).
export const BILINGUAL_BADGE_TEXT = "English + తెలుగు";
export const BILINGUAL_EDITION_LABEL = "Bilingual English–Telugu Digital Edition";
export const LANGUAGES_LABEL = "English + Telugu";

// Shared publisher facts (from each book's legal page)
export const BOOKS_IMPRINT = {
  author: "K Sudheer Bose",
  publisher: "LifeIQ Publications",
  line: "First Edition, 2026 · © 2026 LifeIQ EdTech LLP · LifeIQ Publications, Hyderabad",
};

/**
 * Series index — future-ready for Level 1/2/3 & Cyber Safety books.
 * `published: false` series are structurally reserved but not shown as
 * purchasable products in the UI.
 */
export const SERIES = [
  {
    id: "chinnu",
    slug: "chinnu",
    name: "Chinnu Talking Adventures",
    name_telugu: "చిన్ని సంభాషణలు",
    tagline: "Everyday communication practice in English + Telugu.",
    description:
      "An interactive bilingual series that helps children practise everyday conversations, questions, feelings and real-life communication — one small step at a time.",
    languages: LANGUAGES_LABEL,
    format: "Printed Book",
    accent: "#F57F17",
    published: true,
    availability: "available",
  },
  {
    id: "lifeiq-l1",
    slug: "lifeiq-level-1",
    name: "LifeIQ Level 1",
    retail_descriptor: "Home Intelligence",
    tagline: "Home Intelligence — for ages 10–12.",
    description: ADMIN_PLACEHOLDER,
    accent: "#004D40",
    published: false,
    availability: "coming_soon",
  },
  {
    id: "lifeiq-l2",
    slug: "lifeiq-level-2",
    name: "LifeIQ Level 2",
    retail_descriptor: "System Intelligence",
    tagline: "System Intelligence — for ages 12–14.",
    description: ADMIN_PLACEHOLDER,
    accent: "#004D40",
    published: false,
    availability: "coming_soon",
  },
  {
    id: "cyber-safety-books",
    slug: "cyber-safety",
    name: "LifeIQ Cyber Fraud & Scam Safety",
    tagline: "Pause. Verify. Report. — the reflex that defends a whole family.",
    description:
      "A 3-book family digital safety series that helps readers recognise the patterns underneath modern fraud, verify independently before acting, and know exactly what to do if something has already gone wrong.",
    accent: "#D4A030",
    published: true,
    availability: "available",
  },
];

/**
 * Future format-system readiness (Command 02 §24) — derived, not
 * hardcoded per-product, since every underlying field (price, slug,
 * product_id) already exists on each product object. print_available
 * stays false everywhere until the owner supplies real print pricing —
 * do NOT flip this publicly without real values.
 */
export function getFormatState(product) {
  return {
    digital_available: isPurchasable(product),
    digital_price: product?.price_value ?? null,
    print_available: false,
    print_price: null,
    print_interest_enabled: true,
    stock_status: "not_applicable",
    shipping_required: false,
  };
}

// Master-storefront moving strip — broad collection concepts, not a
// per-book ticker (Command 02 §4: scales cleanly as the catalogue grows).
export const LIFEIQ_LIBRARY_CONCEPTS = [
  "COMMUNICATION",
  "DIGITAL SAFETY",
  "SELF-MANAGEMENT",
  "PEOPLE",
  "MONEY",
  "LIFE DESIGN",
  "EVERYDAY CAPABILITY",
];

/**
 * Chinnu products. Each entry is fully data-driven — the product page
 * template renders whatever is here. `price` is the launch-offer price;
 * `regular_price` is always shown struck through beside it.
 */
export const CHINNU_BOOKS = [
  {
    product_id: "CHINNU-B01",
    slug: "my-day",
    book_number: 1,
    title: "My Day",
    title_telugu: "నా రోజు",
    subtitle: "An Interactive English–Telugu Communication Book",
    series_ladder: "I can answer.",
    short_description:
      "Chinnu helps your child answer in full sentences, ask for what they need, and speak up by themselves — talking together all day long: waking up, eating, playing, and saying good night.",
    key_benefit: "Read · Answer · Speak Up — the everyday talking habit.",
    practise: [
      "Morning routines and getting ready for the day",
      "Eating, hunger and food choices at every meal",
      "Classroom talk and making friends at school",
      "Naming feelings, staying safe, and winding down at bedtime",
    ],
    inside: [
      "58 pages · 12 chapters — “Our Whole Day Together”",
      "Two talking activities in every chapter: Question Time with Chinnu + Now YOU Talk First!",
      "Star Jar reward page after every chapter",
      "English + Telugu side by side throughout",
    ],
    chapters: [
      { name: "Waking Up & Morning Habits", desc: "Morning routines and getting ready for the day" },
      { name: "Bathing & Getting Dressed", desc: "Bath time and choosing clothes" },
      { name: "Breakfast & Food", desc: "Eating, hunger and food choices" },
      { name: "Getting Ready for School", desc: "Packing up and heading out the door" },
      { name: "At School", desc: "Classroom talk and making friends" },
      { name: "Lunch & Afternoon", desc: "Lunchtime and coming home" },
      { name: "Evening & Play", desc: "Playtime and screen time" },
      { name: "Dinner & Manners", desc: "Politeness and saying sorry" },
      { name: "Feelings", desc: "Naming and sharing feelings" },
      { name: "Staying Safe", desc: "Everyday safety, trusted grown-ups and getting help" },
      { name: "Staying Healthy", desc: "Feeling unwell and asking for care" },
      { name: "Bedtime & Family", desc: "Winding down and family time" },
    ],
    parent_note:
      "This book is built to be read with your child — Chinnu asks the questions and cheers the wins, so it feels like play, not a lesson. If your child is tired or distracted, return to the situation later.",
    pages: 58,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    languages: LANGUAGES_LABEL,
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/my-day.jpg",
    preview_assets: [
      "/api/book-assets/previews/my-day/p3.jpg",
      "/api/book-assets/previews/my-day/p4.jpg",
      "/api/book-assets/previews/my-day/p5.jpg",
      "/api/book-assets/previews/my-day/p6.jpg",
      "/api/book-assets/previews/my-day/p7.jpg",
      "/api/book-assets/previews/my-day/p8.jpg",
    ],
    razorpay_payment_link: "https://rzp.io/rzp/sScD0qsK",
    download_asset: "/api/books/download/my-day",
    download_ready: true,
    print_product_id: "CHINNU-PRINT-B01",
    print_price: "₹249",
    print_price_value: 249,
  },
  {
    product_id: "CHINNU-B02",
    slug: "my-world",
    book_number: 2,
    title: "My World",
    title_telugu: "నా ప్రపంచం",
    subtitle: "An Interactive English–Telugu Real-Life Communication Book",
    series_ladder: "I can speak up in the world.",
    short_description:
      "Twelve real-world adventures — friends, classrooms, shops, buses, festivals — that take the talking habit out of the house and into the world, one real place at a time.",
    key_benefit: "Speak · Ask · Connect — confidence outside the home.",
    practise: [
      "Making friends, listening and joining in at school",
      "Talking about weather, animals and the world around us",
      "Buying, asking and saying thank you at shops and markets",
      "Phones, doors and visitors — handled safely",
    ],
    inside: [
      "54 pages · 12 adventures — “Our World to Explore”",
      "Two talking activities in every adventure: Question Time with Chinnu + Now YOU Talk First!",
      "Star Jar reward page after every adventure",
      "English + Telugu side by side throughout",
    ],
    chapters: [
      { name: "Friends at School", desc: "Making friends and playing together" },
      { name: "Classroom Talk", desc: "Listening, asking and joining in" },
      { name: "Weather & Seasons", desc: "Talking about sun, rain and cold" },
      { name: "Animals & Nature", desc: "Animals, birds and plants around us" },
      { name: "Going Places", desc: "Travelling by bus, car and train" },
      { name: "At the Shop & Market", desc: "Buying, asking and saying thank you" },
      { name: "Community Helpers", desc: "Doctors, farmers and helpers" },
      { name: "Places in My Town", desc: "Parks, libraries and getting around" },
      { name: "Festivals & Birthdays", desc: "Celebrating and wishing others" },
      { name: "Helping at Home", desc: "Chores and helping the family" },
      { name: "On the Phone & Guests", desc: "Phones, doors and visitors safely" },
      { name: "Problem-Solving & Play Acting", desc: "Trying, asking and role-play" },
    ],
    parent_note:
      "Whenever you can, take a chapter into the real place — a real shop, a real park. That is where the words come alive.",
    pages: 54,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    languages: LANGUAGES_LABEL,
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/my-world.jpg",
    preview_assets: [
      "/api/book-assets/previews/my-world/p3.jpg",
      "/api/book-assets/previews/my-world/p4.jpg",
      "/api/book-assets/previews/my-world/p5.jpg",
      "/api/book-assets/previews/my-world/p6.jpg",
      "/api/book-assets/previews/my-world/p7.jpg",
      "/api/book-assets/previews/my-world/p8.jpg",
    ],
    razorpay_payment_link: "https://rzp.io/rzp/LVyCjFo",
    download_asset: "/api/books/download/my-world",
    download_ready: true,
    print_product_id: "CHINNU-PRINT-B02",
    print_price: "₹249",
    print_price_value: 249,
  },
  {
    product_id: "CHINNU-B03",
    slug: "ask-me-anything",
    book_number: 3,
    title: "Ask Me Anything!",
    title_telugu: "నన్ను ఏదైనా అడుగు!",
    subtitle: "An Interactive English–Telugu Question-Asking Book",
    series_ladder: "I can ask my own questions.",
    short_description:
      "Pointing and saying “that” can be the beginning of a question. Book 3 turns that point into “What is that?” — with Chinnu's six magic question words and twelve adventures in asking.",
    key_benefit: "What · Where · Who · When · Why · How — the six magic question words.",
    practise: [
      "Asking about things, places, people and time",
      "Asking about reasons (Why?) and ways & feelings (How?)",
      "Telling questions apart from statements — detective quiz",
      "Asking for more, and finding answers together",
    ],
    inside: [
      "54 pages · 12 question adventures",
      "Chinnu's Six Magic Question Words: What, Where, Who, When, Why, How",
      "Question-or-Statement detective quiz, matching game and cheering pages",
      "English + Telugu side by side throughout",
    ],
    chapters: [
      { name: "What Is This?", desc: "Ask about things" },
      { name: "Where Is It?", desc: "Ask about places" },
      { name: "Who Is This?", desc: "Ask about people" },
      { name: "When Do We…?", desc: "Ask about time" },
      { name: "Why?", desc: "Ask about reasons" },
      { name: "How…?", desc: "Ask about ways & feelings" },
      { name: "What Happens Next?", desc: "Ask about order" },
      { name: "Question or Statement?", desc: "Detective quiz" },
      { name: "Match It Up!", desc: "Matching game" },
      { name: "Chinnu Cheers You On!", desc: "Cheering words" },
      { name: "Can You Tell Me More?", desc: "Ask for more" },
      { name: "How Can We Find Out?", desc: "Find answers" },
    ],
    parent_note:
      "When your child is genuinely curious about something in real life, that's the best moment to say “Ask me!” instead of just answering. Let the question come from them.",
    pages: 54,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    languages: LANGUAGES_LABEL,
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/ask-me-anything.jpg",
    preview_assets: [
      "/api/book-assets/previews/ask-me-anything/p3.jpg",
      "/api/book-assets/previews/ask-me-anything/p4.jpg",
      "/api/book-assets/previews/ask-me-anything/p5.jpg",
      "/api/book-assets/previews/ask-me-anything/p6.jpg",
      "/api/book-assets/previews/ask-me-anything/p7.jpg",
      "/api/book-assets/previews/ask-me-anything/p8.jpg",
    ],
    razorpay_payment_link: "https://rzp.io/rzp/KrB4PSlr",
    download_asset: "/api/books/download/ask-me-anything",
    download_ready: true,
    print_product_id: "CHINNU-PRINT-B03",
    print_price: "₹249",
    print_price_value: 249,
  },
  {
    product_id: "CHINNU-B04",
    slug: "why-i-feel-this-way",
    book_number: 4,
    title: "Why I Feel This Way",
    title_telugu: "నాకు ఇలా ఎందుకు అనిపిస్తుంది?",
    subtitle: "An Interactive English–Telugu Feelings & Safety Book",
    series_ladder: "I can explain how I feel and why.",
    short_description:
      "Every feeling has a reason — and every reason can be said out loud with one small word: because. Five feelings chapters, then ten real-moment adventures in staying safe.",
    key_benefit: "Feel · Explain · Stay Safe — emotional vocabulary plus body-safety basics.",
    practise: [
      "Saying feelings with their reasons: scared, angry, sad, proud, worried",
      "Real-moment safety: water, roads, phones, animals, celebrations",
      "Staying close to trusted grown-ups and keeping myself safe",
      "Taking calm breaths when feelings get big",
    ],
    inside: [
      "70 pages · Part A “Because I Feel…” + Part B “Real Moments”",
      "“Safe or Not Safe?” game and calm-breathing practice",
      "Activities, rewards, worksheets and a grown-ups guide",
      "English + Telugu side by side throughout",
    ],
    chapters: [
      { name: "Because I Feel Scared or Calm", desc: "Part A · feelings with reasons" },
      { name: "Because I Feel Angry or Excited", desc: "Part A · feelings with reasons" },
      { name: "Because I Feel Sad or Surprised", desc: "Part A · feelings with reasons" },
      { name: "Because I Feel Proud or Frustrated", desc: "Part A · feelings with reasons" },
      { name: "Because I Feel Worried", desc: "Part A · feelings with reasons" },
      { name: "Playing in Water Safely", desc: "Part B · real moments" },
      { name: "With My Family", desc: "Part B · real moments" },
      { name: "Walking & Crossing Carefully", desc: "Part B · real moments" },
      { name: "Asking for Help & Climbing", desc: "Part B · real moments" },
      { name: "Being Gentle with Animals", desc: "Part B · real moments" },
      { name: "Using the Phone with Permission", desc: "Part B · real moments" },
      { name: "Celebrating Safely", desc: "Part B · real moments" },
      { name: "Waiting My Turn & Playing with Friends", desc: "Part B · real moments" },
      { name: "Staying Close to Trusted Grown-Ups", desc: "Part B · keeping myself safe" },
      { name: "Taking Calm Breaths", desc: "Part B · calming down" },
    ],
    parent_note:
      "When your child is calm enough and ready to talk, gently ask, “Do you know why you feel that way?” If they do not know yet, reassure them that this is okay.",
    pages: 70,
    regular_price: "₹149",
    price: "₹99",
    regular_price_value: 149,
    price_value: 99,
    savings: "₹50",
    offer_label: "Launch Offer",
    languages: LANGUAGES_LABEL,
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/why-i-feel-this-way.jpg",
    preview_assets: [
      "/api/book-assets/previews/why-i-feel-this-way/p3.jpg",
      "/api/book-assets/previews/why-i-feel-this-way/p4.jpg",
      "/api/book-assets/previews/why-i-feel-this-way/p5.jpg",
      "/api/book-assets/previews/why-i-feel-this-way/p6.jpg",
      "/api/book-assets/previews/why-i-feel-this-way/p7.jpg",
      "/api/book-assets/previews/why-i-feel-this-way/p8.jpg",
    ],
    razorpay_payment_link: "https://rzp.io/rzp/AucdHdi",
    download_asset: "/api/books/download/why-i-feel-this-way",
    download_ready: true,
    print_product_id: "CHINNU-PRINT-B04",
    print_price: "₹299",
    print_price_value: 299,
  },
];

export const CHINNU_BUNDLE = {
  product_id: "CHINNU-BUNDLE-04",
  slug: "complete-collection",
  title: "The Complete Chinnu Collection",
  title_telugu: "చిన్ని సంభాషణలు — పూర్తి సంకలనం",
  description:
    "4 interactive bilingual English–Telugu printed books in one collection — the full journey from everyday chatter to quiet confidence, delivered to your door.",
  languages: LANGUAGES_LABEL,
  format: "Printed Book",
  regular_price: "₹446",
  price: "₹199",
  regular_price_value: 446,
  price_value: 199,
  savings: "₹247",
  offer_label: "Collection Offer",
  razorpay_payment_link: "https://rzp.io/rzp/myED6m3y",
  download_asset: "/api/books/download/complete-collection",
  download_ready: true,
  print_product_id: "CHINNU-PRINT-BUNDLE-04",
  print_price: "₹999",
  print_price_value: 999,
  print_individual_total: "₹1,046",
  print_individual_total_value: 1046,
  print_savings: "₹47",
};

export const CHINNU_FAQ = [
  {
    q: "What is Chinnu Talking Adventures?",
    a: "Chinnu Talking Adventures is LifeIQ's bilingual English–Telugu book series built around everyday communication, questions, feelings and real-life conversations children can practise with their families.",
  },
  {
    q: "Are the Chinnu books in English or Telugu?",
    a: "Both. Chinnu Talking Adventures is a bilingual English–Telugu series. The books are designed so families can read, understand and practise across English and Telugu.",
  },
  {
    q: "Are printed books available now?",
    a: "Yes. Printed editions of the Chinnu Talking Adventures books can be ordered directly from the LifeIQ Books store.",
  },
  {
    q: "Can I buy the books individually?",
    a: "Yes. My Day — ₹249. My World — ₹249. Ask Me Anything! — ₹249. Why I Feel This Way — ₹299.",
  },
  {
    q: "Is there a complete collection?",
    a: "Yes. The complete printed Chinnu Talking Adventures collection includes all four books for ₹999. Buying the four books individually totals ₹1,046, so the complete collection saves ₹47.",
  },
  {
    q: "How do I order a printed book?",
    a: "Choose the book or complete collection, enter your delivery and contact details, review the order, and continue to secure online payment through Razorpay. Your order is confirmed only after successful payment verification.",
  },
  {
    q: "What information do I need to provide?",
    a: "We need your name, mobile number, email address and complete delivery address so that we can process and deliver your order.",
  },
  {
    q: "How will I know my order is confirmed?",
    a: "After successful verified payment, the website will show your LifeIQ order confirmation and a confirmation email will be sent to the email address provided during checkout.",
  },
  {
    q: "What happens after I place the order?",
    a: "LifeIQ will prepare and pack your printed books. When the order is dispatched, tracking or dispatch details will be shared when available.",
  },
  {
    q: "What if I enter the wrong address?",
    a: "Contact LifeIQ as soon as possible before dispatch: LifeIqedtech@gmail.com or WhatsApp +91 9059112617. Once an order has been dispatched, changing the delivery address may not be possible.",
  },
  {
    q: "What if money is deducted but I do not see an order confirmation?",
    a: "Do not make repeated payments immediately. Contact LifeIQ with your payment details so the payment can be checked against the order record. Email: LifeIqedtech@gmail.com. WhatsApp: +91 9059112617.",
  },
  {
    q: "Can I order multiple copies for a school or group?",
    a: "Yes. For larger quantities, contact LifeIQ before placing the order so the requirement can be discussed. Email: LifeIqedtech@gmail.com. WhatsApp: +91 9059112617.",
  },
  {
    q: "How can I contact LifeIQ about my order?",
    a: "Email: LifeIqedtech@gmail.com. WhatsApp: +91 9059112617.",
  },
];

// Chinnu-specific co-reading philosophy — used on /books/chinnu only.
export const CHINNU_MANIFESTO = [
  {
    n: "01",
    title: "Children learn to talk by talking.",
    body: "Not by being read at, but by being read with. Every Chinnu page is built to hand the conversation to your child — a question to answer, a choice to make, a feeling to name.",
  },
  {
    n: "02",
    title: "Small moments, practised often, become character.",
    body: "A greeting at breakfast. A question at the shop. A feeling named before it overflows. Confidence is not a lesson — it is a hundred tiny repetitions at home.",
  },
  {
    n: "03",
    title: "Two languages, one confident voice.",
    body: "Chinnu speaks English and Telugu side by side, the way our homes actually sound. Children practise expressing themselves in the words they hear every day — and the words the world will ask of them.",
  },
];

// Master /books storefront manifesto — catalogue-neutral, spans every
// LifeIQ Books series (Chinnu, Cyber Safety, and future titles).
export const LIFEIQ_BOOKS_MANIFESTO = [
  {
    n: "01",
    title: "Real life needs practice, not just information.",
    body: "Knowing something is different from being able to use it when the moment arrives.",
  },
  {
    n: "02",
    title: "Good books should change what you do next.",
    body: "A conversation to practise, a decision to make, a warning sign to recognise, or a tool to carry into daily life.",
  },
  {
    n: "03",
    title: "Families learn better when they can talk about real situations together.",
    body: "LifeIQ books are designed to create useful conversations between children, young people and adults.",
  },
];

// Bilingual marquee ribbon phrases (English / authoritative Telugu titles).
export const MARQUEE_PHRASES = [
  { en: "My Day", te: "నా రోజు" },
  { en: "My World", te: "నా ప్రపంచం" },
  { en: "Ask Me Anything!", te: "నన్ను ఏదైనా అడుగు!" },
  { en: "Why I Feel This Way", te: "నాకు ఇలా ఎందుకు అనిపిస్తుంది?" },
  { en: "Little Conversations", te: "చిన్ని సంభాషణలు" },
  { en: "Read Together", te: "కలిసి చదుదాం" },
];

// Quick-start tips shown on the post-purchase download page.
export const QUICKSTART_TIPS = [
  { n: "1", t: "Pick one scene, not the whole book", d: "A single page is a complete conversation. Stop while it's still fun." },
  { n: "2", t: "Ask, then wait", d: "Count slowly to five after every question. Children answer in the silence we usually fill." },
  { n: "3", t: "Any language counts", d: "English, Telugu or a joyful mix of both — expression first, grammar later." },
  { n: "4", t: "Repeat the favourites", d: "Re-reading the same scene is not boring for a child. Repetition is the practice." },
];

// Purchase support contacts (owner-confirmed, Command 03; email casing updated per owner)
export const BOOKS_SUPPORT = {
  email: "LifeIqedtech@gmail.com",
  whatsapp: "+91 9059112617",
  whatsapp_link: "https://wa.me/919059112617",
};

// Author — reusable across Chinnu + future LifeIQ Publications titles.
export const CHINNU_AUTHOR = {
  name: "K Sudheer Bose",
  role: "Author, Chinnu Talking Adventures · LifeIQ Publications",
  bio:
    "K Sudheer Bose created Chinnu's Talking Adventures for LifeIQ Publications — four bilingual English–Telugu books written to be read aloud together, so children practise real conversation, not just recognise words. Every page is built around one belief: children learn to talk by talking, with a parent right beside them.",
  photo: "/api/book-assets/author/k-sudheer-bose-author.jpg",
  photo_alt: "K Sudheer Bose, author of Chinnu's Talking Adventures",
};

// Helpers
export const isReady = (v) =>
  v !== undefined && v !== null && v !== "" && v !== ADMIN_PLACEHOLDER;

export const isPurchasable = (product) => isReady(product?.price) && isReady(product?.product_id);

/** Command 04: Chinnu's public sales mode is physical-first — this checks
 * the NEW printed-book product fields, completely separate from the
 * digital isPurchasable() above (which still backs historic order pages). */
export const isPrintPurchasable = (product) => isReady(product?.print_price) && isReady(product?.print_product_id);

export const findChinnuBook = (slug) => CHINNU_BOOKS.find((b) => b.slug === slug);

/** Find any purchasable Chinnu product — a single book or the bundle. */
export const findChinnuProduct = (slug) =>
  slug === CHINNU_BUNDLE.slug ? CHINNU_BUNDLE : findChinnuBook(slug);
